export interface DelegationStateLike<RoleType extends string = string> {
  isDelegating: boolean;
  delegatedTenantName?: string;
  delegatedTenantId?: string;
  delegatedRole?: RoleType;
}

export interface DelegatedSessionLike<RoleType extends string = string> {
  delegationSessionId: string;
  targetTenantId: string;
  targetTenantName: string;
  effectiveRole: RoleType;
  actorRole?: RoleType;
  expiresAt: string;
}

export interface DelegatedSessionApiResponse<RoleType extends string = string> {
  delegationSessionId: string;
  targetTenantId: string;
  targetTenantName?: string;
  effectiveRole: RoleType;
  actorRole?: RoleType;
  expiresAt: string;
}

declare global {
  interface Window {
    __timeshiftDelegationDebug?: {
      getState: () => unknown;
    };
  }
}

export const mapDelegatedSession = <RoleType extends string>(
  session: DelegatedSessionApiResponse<RoleType>,
  fallbackTenantName?: string
): DelegatedSessionLike<RoleType> => ({
  delegationSessionId: session.delegationSessionId,
  targetTenantId: session.targetTenantId,
  targetTenantName: session.targetTenantName ?? fallbackTenantName ?? '',
  effectiveRole: session.effectiveRole,
  actorRole: session.actorRole,
  expiresAt: session.expiresAt
});

export const toUserDelegation = <RoleType extends string>(
  delegatedSession?: DelegatedSessionLike<RoleType>
): DelegationStateLike<RoleType> => ({
  isDelegating: !!delegatedSession,
  delegatedTenantId: delegatedSession?.targetTenantId,
  delegatedTenantName: delegatedSession?.targetTenantName,
  delegatedRole: delegatedSession?.effectiveRole
});

export const getDelegationHeaders = (delegatedSessionId?: string) => {
  const headers: Record<string, string> = {};
  if (delegatedSessionId) {
    headers['x-delegation-session-id'] = delegatedSessionId;
  }
  return headers;
};

export const loadCurrentDelegatedSession = async <RoleType extends string>(
  baseUrl: string,
  accessToken?: string
) => {
  if (!accessToken) {
    return undefined;
  }

  try {
    const response = await fetch(`${baseUrl}/delegation-sessions/current`, {
      headers: {
        authorization: `Bearer ${accessToken}`,
        accept: 'application/json'
      }
    });

    if (response.status === 404) {
      return undefined;
    }

    if (!response.ok) {
      throw new Error(`Failed to load current delegation: ${response.status}`);
    }

    const session = (await response.json()) as DelegatedSessionApiResponse<RoleType>;
    return mapDelegatedSession(session);
  } catch {
    return undefined;
  }
};

export const renewDelegatedSessionRequest = async <RoleType extends string>(
  baseUrl: string,
  accessToken: string,
  delegatedSessionId?: string
) => {
  const response = await fetch(`${baseUrl}/delegation-sessions/current/renew`, {
    method: 'POST',
    headers: {
      authorization: `Bearer ${accessToken}`,
      accept: 'application/json',
      ...getDelegationHeaders(delegatedSessionId)
    }
  });

  if (response.status === 404 || response.status === 403) {
    return { status: 'missing' as const };
  }

  if (!response.ok) {
    throw new Error(`Failed to renew delegation: ${response.status}`);
  }

  const session = (await response.json()) as DelegatedSessionApiResponse<RoleType>;
  return { status: 'ok' as const, session: mapDelegatedSession(session) };
};

export const startDelegatedSessionRequest = async <RoleType extends string>(
  baseUrl: string,
  accessToken: string,
  request: {
    targetTenantId: string;
    effectiveRole: RoleType;
  },
  fallbackTenantName?: string
) => {
  const response = await fetch(`${baseUrl}/delegation-sessions`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${accessToken}`,
      accept: 'application/json'
    },
    body: JSON.stringify(request)
  });

  if (!response.ok) {
    throw new Error(`Failed to start delegation: ${response.status}`);
  }

  const session = (await response.json()) as DelegatedSessionApiResponse<RoleType>;
  return mapDelegatedSession(session, fallbackTenantName);
};

export const endDelegatedSessionRequest = async (
  baseUrl: string,
  accessToken: string,
  delegatedSessionId?: string
) => {
  const response = await fetch(`${baseUrl}/delegation-sessions/current`, {
    method: 'DELETE',
    headers: {
      authorization: `Bearer ${accessToken}`,
      accept: 'application/json',
      ...getDelegationHeaders(delegatedSessionId)
    }
  });

  if (response.status === 404 || response.status === 403) {
    return { status: 'missing' as const };
  }

  if (!response.ok) {
    throw new Error(`Failed to end delegation: ${response.status}`);
  }

  return { status: 'ended' as const };
};

export const createDelegationDebugTools = (enabled: boolean) => {
  const log = (event: string, details?: Record<string, unknown>) => {
    if (!enabled) {
      return;
    }

    console.info(`[delegation] ${event}`, details ?? {});
  };

  const register = (getState: () => unknown) => {
    if (!enabled) {
      return;
    }

    window.__timeshiftDelegationDebug = { getState };
    log('debug-helper-registered', {
      helper: 'window.__timeshiftDelegationDebug.getState()'
    });
  };

  return { log, register };
};

export const createDelegationRenewalController = (options: {
  renewLeadMs: number;
  getExpiresAt: () => string | undefined;
  onRenew: () => Promise<void>;
  onClear: () => void;
}) => {
  let renewTimer: number | undefined;

  const sync = () => {
    if (renewTimer) {
      clearTimeout(renewTimer);
      renewTimer = undefined;
    }

    const expiresAt = options.getExpiresAt();
    if (!expiresAt) {
      return;
    }

    const expiresAtMs = new Date(expiresAt).getTime();
    const delayMs = Math.max(expiresAtMs - Date.now() - options.renewLeadMs, 0);

    renewTimer = window.setTimeout(() => {
      void options.onRenew().catch(() => options.onClear());
    }, delayMs);
  };

  return { sync };
};
